import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    AppBar,
    Toolbar,
    Slide,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Close as CloseIcon,
    Clear as ClearIcon,
    Palette as PaletteIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import html2canvas from 'html2canvas';
import { useMediaQuery } from '@mui/material';
import { useFeedback } from '../support-hub/SupportHubContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useLocation, useNavigate } from 'react-router-dom';
import { PostSubmitPanel } from './PostSubmitPanel';
import { isStaffUser, realUserId, seenAsUserId } from './lodger';
import { ISSUE_EVIDENCE_ACCEPT } from './issueEvidence';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface Point {
    x: number;
    y: number;
}

interface Stroke {
    points: Point[];
    color: string;
    width: number;
}




export const FeedbackCaptureOverlay: React.FC = () => {
    const { isOverlayOpen, closeOverlay, recentErrors } = useFeedback();
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    // Staff as the database decides it (is_platform_admin), not `platform:view_all`, which 4 of the 11 admins lack.
    // Under impersonation `user` is the impersonated account, so the real admin comes from impersonated_by_admin_id.
    const isAdmin = isStaffUser(user);

    const [isCapturing, setIsCapturing] = useState(false);
    const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
    const [extraImages, setExtraImages] = useState<{file: File, previewUrl: string}[]>([]);

    // Form State
    const [userNotes, setUserNotes] = useState('');
    const [what, setWhat] = useState('');
    const [how, setHow] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [lastErrorCount, setLastErrorCount] = useState(0);
    const [assigneeId, setAssigneeId] = useState<string>('');
    const [priority, setPriority] = useState<string>('tbd');
    const [pendingPriority, setPendingPriority] = useState<string | null>(null);
    const [platformAdmins, setPlatformAdmins] = useState<{ id: string; email: string }[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Post-submit "morph in place" flow: instead of closing on submit, the right
    // panel hands off to PostSubmitPanel (analyzing → clarifying questions → done).
    const [postSubmit, setPostSubmit] = useState(false);
    const [submittedIssueId, setSubmittedIssueId] = useState<string | null>(null);

    const isValid = what.trim().length >= 20;

    const handlePriorityChange = (newValue: string) => {
        if (newValue === 'urgent' || newValue === 'critical') {
            setPendingPriority(newValue);
        } else {
            setPriority(newValue);
        }
    };

    const confirmPriority = () => {
        if (pendingPriority) setPriority(pendingPriority);
        setPendingPriority(null);
    };

    const cancelPriority = () => setPendingPriority(null);

    // Drawing State
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [strokes, setStrokes] = useState<Stroke[]>([]);
    const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null);
    const [strokeColor, setStrokeColor] = useState('#ff0000');

    useEffect(() => {
        if (isOverlayOpen) {
            captureScreen();
            fetchPlatformAdmins();
        } else {
            // Reset state on close
            setScreenshotDataUrl(null);
            extraImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
            setExtraImages([]);
            setStrokes([]);
            setCurrentStroke(null);
            setUserNotes('');
            setWhat('');
            setHow('');
            setErrorMessage('');
            setLastErrorCount(0);
            setAssigneeId('');
            setPriority('tbd');
            setPostSubmit(false);
            setSubmittedIssueId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOverlayOpen]);

    // Continuously sync new errors into the error message field while the overlay is open
    useEffect(() => {
        if (isOverlayOpen && recentErrors && recentErrors.length > lastErrorCount) {
            const newErrors = recentErrors.slice(lastErrorCount);
            setErrorMessage(prev => {
                if (!prev) return newErrors.join('\n\n---\n\n');
                return prev + '\n\n---\n\n' + newErrors.join('\n\n---\n\n');
            });
            setLastErrorCount(recentErrors.length);
        }
    }, [isOverlayOpen, recentErrors, lastErrorCount]);

    const fetchPlatformAdmins = async () => {
        try {
            if (!isAdmin) {
                if (user) {
                    setPlatformAdmins([{ id: user.id, email: user.email || 'You' }]);
                }
                return;
            }

            // Try fetching via an RPC.
            const { data, error } = await supabase.rpc('get_platform_admins');

            if (data && !error) {
                setPlatformAdmins(data as { id: string; email: string }[]);
            } else {
                // Fallback: just put the current (real) user
                if (user) {
                    setPlatformAdmins([{ id: realUserId(user) ?? user.id, email: user.impersonated_by_admin_id ? 'You' : user.email || 'You' }]);
                }
            }
        } catch (err) {
            console.error('Failed to fetch platform admins', err);
        }
    };

    const captureScreen = async () => {
        setIsCapturing(true);
        try {
            // Hide the floating button temporarily during capture
            const fab = document.querySelector('.MuiFab-root') as HTMLElement;
            if (fab) fab.style.display = 'none';

            const canvas = await html2canvas(document.body, {
                useCORS: true,
                allowTaint: true,
                logging: false,
                ignoreElements: (element) => 
                    element.id === 'feedback-overlay-container' || 
                    element.classList.contains('recharts-tooltip-wrapper'),
            });

            setScreenshotDataUrl(canvas.toDataURL('image/jpeg', 0.8));

            if (fab) fab.style.display = 'inline-flex';
        } catch (error) {
            console.error('Screenshot failed:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to capture screenshot');
        } finally {
            setIsCapturing(false);
        }
    };

    // Canvas Drawing Methods
    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        setIsDrawing(true);
        const point = getCoordinates(e);
        if (!point) return;
        setCurrentStroke({ points: [point], color: strokeColor, width: 3 });
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        if (!isDrawing || !currentStroke) return;
        const point = getCoordinates(e);
        if (!point) return;
        setCurrentStroke((prev) => prev ? { ...prev, points: [...prev.points, point] } : null);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        if (currentStroke) {
            setStrokes((prev) => [...prev, currentStroke]);
            setCurrentStroke(null);
        }
    };

    const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): Point | null => {
        if (!canvasRef.current) return null;
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // Calculate scale factors
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        let clientX, clientY;
        if ('touches' in e) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = (e as React.MouseEvent).clientX;
            clientY = (e as React.MouseEvent).clientY;
        }

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        };
    };

    // Re-draw canvas when strokes change
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const drawStroke = (stroke: Stroke) => {
            if (stroke.points.length < 2) return;
            ctx.beginPath();
            ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
            for (let i = 1; i < stroke.points.length; i++) {
                ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
            }
            ctx.strokeStyle = stroke.color;
            ctx.lineWidth = stroke.width;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.stroke();
        };

        strokes.forEach(drawStroke);
        if (currentStroke) drawStroke(currentStroke);
    }, [strokes, currentStroke]);

    const undoLastStroke = () => {
        setStrokes((prev) => prev.slice(0, -1));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            let finalScreenshotUrl = null;

            if (screenshotDataUrl) {
                // To avoid CSP issues with 'fetch(data:...)' we will draw our final composite 
                // image onto a single canvas and extract a Blob directly via canvas.toBlob()

                const finalCanvas = document.createElement('canvas');
                finalCanvas.width = imgDimensions.width;
                finalCanvas.height = imgDimensions.height;
                const ctx = finalCanvas.getContext('2d');

                if (ctx) {
                    const img = new Image();
                    img.src = screenshotDataUrl; // data URIs are fine for an Image element src
                    await new Promise((resolve) => { img.onload = resolve; });

                    // Draw the base screenshot
                    ctx.drawImage(img, 0, 0);

                    // Overlay the user strokes if they exist
                    if (canvasRef.current && strokes.length > 0) {
                        ctx.drawImage(canvasRef.current, 0, 0);
                    }

                    // Extract the final image natively as a Blob (bypassing the need for fetch)
                    const blob = await new Promise<Blob>((resolve, reject) => {
                        finalCanvas.toBlob((b) => {
                            if (b) resolve(b);
                            else reject(new Error('Failed to create blob from canvas'));
                        }, 'image/jpeg', 0.8);
                    });

                    const pathPrefix = user ? user.id : 'public';
                    try {
                        const fileName = `${pathPrefix}/${Date.now()}-screenshot-${crypto.randomUUID()}.jpg`;
                        const { data: uploadData, error: uploadError } = await supabase.storage
                            .from('issue_evidence')
                            .upload(fileName, blob, { contentType: 'image/jpeg' });

                        if (uploadError) throw uploadError;
                        if (uploadData) {
                            finalScreenshotUrl = uploadData.path;
                        }
                    } catch (uploadErr) {
                        console.error('Failed to upload feedback screenshot to storage:', uploadErr);
                    }
                }
            }

            // Upload extra images in parallel
            const pathPrefix = user ? user.id : 'public';
            const extraUploadPromises = extraImages.map(async ({ file }) => {
                try {
                    const fileExt = file.name.split('.').pop() || 'png';
                    const fileName = `${pathPrefix}/${Date.now()}-extra-${crypto.randomUUID()}.${fileExt}`;
                    const { data, error } = await supabase.storage
                        .from('issue_evidence')
                        .upload(fileName, file);
                    if (error) throw error;
                    return data?.path;
                } catch (uploadErr) {
                    console.error('Failed to upload extra feedback image:', uploadErr);
                    return null;
                }
            });

            const extraPaths = (await Promise.all(extraUploadPromises)).filter(Boolean) as string[];

            // "Who it affects": the account the fault was seen in, which is the impersonated user's while a staff
            // member is viewing the app as them.
            const autoWho = user?.email || user?.id || 'Web user';
            const clientMetadata = {
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                screen: typeof window !== 'undefined' ? {
                    width: window.innerWidth,
                    height: window.innerHeight,
                    devicePixelRatio: window.devicePixelRatio
                } : null,
                route: location.pathname,
            };

            const seenAs = seenAsUserId(user);

            // 2. Insert into database
            const payload = {
                url_path: location.pathname,
                full_url: window.location.href,
                error_message: errorMessage || null,
                user_notes: userNotes?.trim() || null,
                who: autoWho,
                what: what.trim(),
                why: null,
                when_text: null,
                where_text: location.pathname,
                how: how.trim() ? how.trim() : null,
                client_metadata: clientMetadata,
                screenshot_url: finalScreenshotUrl,
                extra_screenshots: extraPaths.length > 0 ? extraPaths : undefined,
                canvas_data: strokes.length > 0 ? JSON.stringify(strokes) : null,
                assignee_id: isAdmin && assigneeId ? assigneeId : null,
                priority: isAdmin ? priority : 'tbd',
                status: 'open',
                source: 'web',
                // The lodger is the real person; the database enforces it (fn_site_issue_set_lodger). seen_as_user_id
                // goes only when set, so an ordinary report still files if the column is not there yet (deploy order).
                ...(user ? { reporter_id: realUserId(user) } : {}),
                ...(seenAs ? { seen_as_user_id: seenAs } : {})
            };

            // maybeSingle (not single) so a public/anon submit — which can INSERT but
            // not SELECT under RLS — still succeeds; we just fall back to the generic
            // confirmation when the id can't be read back. The insert trigger owns
            // triage_status; the post-submit panel drives its own "analyzing" state.
            const { data: inserted, error: insertError } = await supabase
                .from('site_issues')
                .insert(payload)
                .select('id')
                .maybeSingle();

            if (insertError) throw insertError;

            // Hand off to the in-place post-submit flow instead of closing. If we
            // could not read the id back (e.g. anonymous submit, no SELECT under
            // RLS), the panel falls back to a generic confirmation.
            setSubmittedIssueId(inserted?.id ?? null);
            setPostSubmit(true);
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            alert('Failed to submit feedback. Check console for details.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTestError = () => {
        console.error("TEST ERROR LOG: This is a simulated error to verify the console capturing logic.", {
            timestamp: new Date().toISOString(),
            testObject: { nested: true, circular: undefined } // Circular structures handled via FeedbackProvider safe stringify
        });
        alert('Simulated error dispatched to console. Check the Error Message field!');
    };

    const handleFileUpload = (file: File) => {
        if (!file.type.startsWith('image/')) {
            alert('Only image files are supported');
            return;
        }
        setExtraImages(prev => [...prev, { file, previewUrl: URL.createObjectURL(file) }]);
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const file = items[i].getAsFile();
                if (file) handleFileUpload(file);
                break;
            }
        }
    };

    const removeExtraImage = (index: number) => {
        setExtraImages(prev => {
            const newImages = [...prev];
            URL.revokeObjectURL(newImages[index].previewUrl);
            newImages.splice(index, 1);
            return newImages;
        });
    };

    // Get original image dimensions to match canvas aspect ratio
    const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });
    useEffect(() => {
        if (screenshotDataUrl) {
            const img = new Image();
            img.onload = () => {
                setImgDimensions({ width: img.width, height: img.height });
            };
            img.src = screenshotDataUrl;
        }
    }, [screenshotDataUrl]);

    return (
        <Dialog
            fullScreen
            open={isOverlayOpen}
            onClose={closeOverlay}
            TransitionComponent={Transition}
            id="feedback-overlay-container"
            PaperProps={{
                sx: { bgcolor: 'background.default' }
            }}
        >
            <AppBar sx={{ position: 'relative' }} color="secondary">
                <Toolbar>
                    <Tooltip title="Close">
                        <IconButton edge="start" color="inherit" onClick={closeOverlay} aria-label="close" sx={{ minWidth: 44, minHeight: 44 }}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography sx={{ ml: 2, flex: 1, color: '#ffffff' }} variant="h6" component="div">
                        Report Issue / Feedback
                    </Typography>
                    {!postSubmit && (
                        <Button
                            autoFocus
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting || isCapturing || !isValid}
                            sx={{
                                bgcolor: 'common.white',
                                color: 'secondary.dark',
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: 'rgba(255, 255, 255, 0.12)',
                                    color: 'rgba(255, 255, 255, 0.35)'
                                }
                            }}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Issue'}
                        </Button>
                    )}
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: 'calc(100vh - 64px)' }} onPaste={handlePaste}>
                {/* Left Side: Screenshot & Canvas — persists through the post-submit
                    flow for continuity, but yields vertical space on small screens
                    once the questions/confirmation panel takes over. */}
                <Box sx={{
                    flex: postSubmit ? { xs: '0 0 auto', md: 3 } : 3,
                    maxHeight: postSubmit ? { xs: '30vh', md: 'none' } : 'none',
                    position: 'relative', bgcolor: '#121212', overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', p: 2 }}>
                    {isCapturing ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white' }}>
                            <CircularProgress color="inherit" sx={{ mb: 2 }} />
                            <Typography>Capturing screen...</Typography>
                        </Box>
                    ) : screenshotDataUrl ? (
                        <Box sx={{ position: 'relative', display: 'inline-block', boxShadow: 3, maxWidth: '100%' }}>
                            <img
                                src={screenshotDataUrl}
                                alt="Screenshot"
                                style={{
                                    display: 'block',
                                    maxWidth: '100%',
                                    height: 'auto',
                                    objectFit: 'contain'
                                }}
                            />
                            {imgDimensions.width > 0 && (
                                <canvas
                                    ref={canvasRef}
                                    width={imgDimensions.width}
                                    height={imgDimensions.height}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseOut={stopDrawing}
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        cursor: postSubmit ? 'default' : 'crosshair',
                                        pointerEvents: postSubmit ? 'none' : 'auto',
                                        touchAction: 'none'
                                    }}
                                />
                            )}

                            {/* Toolbar floating over the image */}
                            {!postSubmit && (
                            <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, bgcolor: 'background.paper', p: 0.5, borderRadius: 1, boxShadow: 2 }}>
                                <Tooltip title="Draw Red">
                                    <IconButton size="small" aria-label="Draw Red" onClick={() => setStrokeColor('#ff0000')} sx={{ color: '#ff0000', minWidth: 44, minHeight: 44 }}><PaletteIcon /></IconButton>
                                </Tooltip>
                                <Tooltip title="Draw Yellow">
                                    <IconButton size="small" aria-label="Draw Yellow" onClick={() => setStrokeColor('#ffd700')} sx={{ color: '#b45309', minWidth: 44, minHeight: 44 }}><PaletteIcon /></IconButton>
                                </Tooltip>
                                <Tooltip title="Undo">
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton size="small" aria-label="Undo last stroke" onClick={undoLastStroke} disabled={strokes.length === 0} sx={{ minWidth: 44, minHeight: 44 }}><UndoIcon /></IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography sx={{ color: 'white', m: 'auto' }}>No screenshot available</Typography>
                    )}
                </Box>

                {/* Right Side: Form → PostSubmitPanel after submission */}
                <Box sx={{ flex: 1, minWidth: { md: '350px' }, maxWidth: { md: '450px' }, borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {postSubmit ? (
                        <PostSubmitPanel
                            issueId={submittedIssueId}
                            isAdmin={isAdmin}
                            reducedMotion={prefersReducedMotion}
                            onClose={closeOverlay}
                            onViewTicket={(id) => { closeOverlay(); navigate(`/admin/issues/${id}`); }}
                        />
                    ) : (
                    <Box sx={{ p: 3, overflowY: 'auto' }}>
                    <Typography variant="h6" gutterBottom>Issue Details</Typography>

                    <TextField
                        fullWidth
                        label="URL Path"
                        value={location.pathname}
                        disabled
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <Typography variant="body2" color="warning.main" sx={{ mb: 2, fontWeight: 'medium' }}>
                        Please make sure you are logged into the correct role/incognito window and check that the bug has not already been submitted.
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        label="What happened & what should happen? *"
                        placeholder="Describe the issue: what went wrong, and what did you expect to happen instead?"
                        value={what}
                        onChange={(e) => setWhat(e.target.value)}
                        error={what.trim().length > 0 && what.trim().length < 20}
                        helperText={
                            what.trim().length < 20
                                ? `⚠️ Minimum 20 characters required. (${what.trim().length}/20)`
                                : `✓ ${what.trim().length} characters`
                        }
                        FormHelperTextProps={{
                            sx: {
                                color: what.trim().length < 20 ? 'warning.main' : 'success.main',
                                fontWeight: what.trim().length < 20 ? 'medium' : 'normal',
                            }
                        }}
                        sx={{ mb: 2 }}
                        required
                    />

                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        label="Steps to Reproduce (Optional)"
                        placeholder="e.g., 1. Open job detail. 2. Go to photos. 3. Upload photo."
                        value={how}
                        onChange={(e) => setHow(e.target.value)}
                        helperText={how.trim().length > 0 ? `✓ ${how.trim().length} characters` : undefined}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        label="Additional Notes (Optional)"
                        placeholder="Paste any additional notes or context here..."
                        value={userNotes}
                        onChange={(e) => setUserNotes(e.target.value)}
                        helperText={userNotes.trim().length > 0 ? `✓ ${userNotes.trim().length} characters` : undefined}
                        sx={{ mb: 2 }}
                    />

                    {isAdmin ? (
                        <Box sx={{ mb: 1 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Error Message (Optional)"
                                placeholder="Paste any error message or JS log here..."
                                value={errorMessage}
                                onChange={(e) => setErrorMessage(e.target.value)}
                                InputProps={{
                                    endAdornment: errorMessage ? (
                                        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                            <IconButton
                                                size="small"
                                                aria-label="Clear error"
                                                onClick={() => setErrorMessage('')}
                                                edge="end"
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    p: 0.5,
                                                    position: 'relative',
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: -6,
                                                        bottom: -6,
                                                        left: -6,
                                                        right: -6,
                                                    }
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                            />
                        </Box>
                    ) : errorMessage ? (
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                            <Chip size="small" color="info" label="Logs Attached" />
                            <Typography variant="caption" color="text.secondary">
                                Technical console error logs have been automatically captured.
                            </Typography>
                        </Box>
                    ) : null}

                    <Box sx={{ mb: 3, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Extra Screenshots</Typography>
                        {extraImages.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                {extraImages.map((img, idx) => (
                                    <Box key={idx} sx={{ position: 'relative', width: 60, height: 60, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                        <img src={img.previewUrl} alt="Extra" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <Tooltip title="Remove extra image">
                                            <IconButton size="small" aria-label="Remove extra image" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }} onClick={() => removeExtraImage(idx)}>
                                                <CloseIcon sx={{ fontSize: 14, color: 'white' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <Button variant="outlined" component="label" size="small">
                            Add Image
                            <input type="file" hidden accept={ISSUE_EVIDENCE_ACCEPT} multiple onChange={(e) => {
                                if (e.target.files) {
                                    Array.from(e.target.files).forEach(handleFileUpload);
                                }
                                e.target.value = '';
                            }} />
                        </Button>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>Or paste (Ctrl+V) anywhere</Typography>
                    </Box>

                    {isAdmin && (
                        <>
                            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={handleTestError}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Trigger Test Error Capture
                                </Button>
                            </Box>

                            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                <InputLabel id="feedback-priority-label">Priority</InputLabel>
                                <Select
                                    labelId="feedback-priority-label"
                                    value={priority}
                                    onChange={(e) => handlePriorityChange(e.target.value)}
                                    label="Priority"
                                    renderValue={(val) => {
                                        const map: Record<string, { color: 'default' | 'success' | 'info' | 'warning' | 'error', label: string, sx?: Record<string, string | number> }> = {
                                            tbd: { color: 'default', label: 'TBD' },
                                            low: { color: 'success', label: 'Low' },
                                            medium: { color: 'info', label: 'Med' },
                                            high: { color: 'warning', label: 'High' },
                                            urgent: { color: 'error', label: 'Urgent' },
                                            critical: { color: 'error', label: 'Critical', sx: { bgcolor: '#8B0000', color: 'white' } },
                                        };
                                        const m = map[val as string] ?? map.tbd;
                                        return <Chip size="small" color={m.color} label={m.label} sx={m.sx || {}} />;
                                    }}
                                >
                                    <MenuItem value="tbd"><Chip size="small" color="default" label="TBD" /></MenuItem>
                                    <MenuItem value="low"><Chip size="small" color="success" label="Low" /></MenuItem>
                                    <MenuItem value="medium"><Chip size="small" color="info" label="Med" /></MenuItem>
                                    <MenuItem value="high"><Chip size="small" color="warning" label="High" /></MenuItem>
                                    <MenuItem value="urgent"><Chip size="small" color="error" label="Urgent" /></MenuItem>
                                    <MenuItem value="critical"><Chip size="small" color="error" label="Critical" sx={{ bgcolor: '#8B0000', color: 'white' }} /></MenuItem>
                                </Select>
                            </FormControl>

                            {/* Urgent / Critical Priority Confirmation Dialog */}
                            <Dialog
                                open={!!pendingPriority}
                                onClose={cancelPriority}
                                maxWidth="xs"
                                fullWidth
                                PaperProps={{ sx: { borderTop: '4px solid', borderColor: pendingPriority === 'critical' ? '#8B0000' : 'error.main' } }}
                            >
                                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WarningAmberIcon color="error" />
                                    {pendingPriority === 'critical' ? 'Critical Priority — Are You Sure?' : 'Urgent Priority — Are You Sure?'}
                                </DialogTitle>
                                <DialogContent>
                                    <Typography variant="body2" color="text.secondary">
                                        Marking this issue as <strong>{pendingPriority === 'critical' ? 'Critical' : 'Urgent'}</strong> means the team will <strong>stop all current work</strong> and redirect full attention to resolving this immediately.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                        Please confirm this issue genuinely warrants that level of interruption.
                                    </Typography>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                                    <Button onClick={cancelPriority} variant="outlined">
                                        Cancel — keep previous priority
                                    </Button>
                                    <Button onClick={confirmPriority} variant="contained" color="error" autoFocus>
                                        Yes, this is {pendingPriority === 'critical' ? 'critical' : 'urgent'}
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                <InputLabel id="feedback-assignee-label">Assignee</InputLabel>
                                <Select
                                    labelId="feedback-assignee-label"
                                    value={assigneeId}
                                    onChange={(e) => setAssigneeId(e.target.value)}
                                    label="Assignee"
                                >
                                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                                    {platformAdmins.map((admin) => (
                                        <MenuItem key={admin.id} value={admin.id}>{admin.email}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        The screenshot on the left is automatically included. You can draw on it to highlight exactly what you mean.
                    </Typography>

                    <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isCapturing || !isValid}
                        sx={{ mt: 3, display: { xs: 'flex', md: 'none' } }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Issue'}
                    </Button>
                    </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
